import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiDownload, FiPrinter, FiRefreshCw } from "react-icons/fi";
import { useAuthFetch } from "../hooks/useAuthFetch";
import { proposalTemplatesService } from "../services/api/proposalTemplatesService";
import {
  fillTemplateHtml,
  sourcesForKeys,
  templateFieldKeys,
} from "../utils/reportFieldCatalog";
import { loadProjectFieldContext } from "../utils/reportFieldData";

const OFFICE_LINE = "Corporate: 2850 Fairfax Trfy, Kansas City, KS 66115 | 816.561.7667";

/** Highlights empty API fields on screen only; print and PDF stay clean. */
const EMPTY_FIELD_STYLE = `<style data-nettles-field-hint>
@media screen {
  .f[data-field]:empty { display:inline-block; min-width:4ch; background:#fef9c3; box-shadow:inset 0 -1px 0 #eab308; padding:0 3px; border-radius:2px; }
  .f[data-field]:empty::after { content:attr(data-ph); color:#a16207; font-size:.8em; font-weight:400; }
}
</style>`;

function prepareHtml(html) {
  let out = String(html || "");
  out = out.split("{{LOGO_SRC}}").join("/assets/nettles_logo_color.png");
  out = out.split("{{CLIENT_LOGO}}").join("");
  out = out.split("{{OFFICE_LINE}}").join(OFFICE_LINE);
  return out;
}

function withHint(html) {
  return /<\/head>/i.test(html)
    ? html.replace(/<\/head>/i, `${EMPTY_FIELD_STYLE}</head>`)
    : `${EMPTY_FIELD_STYLE}${html}`;
}

/**
 * A saved report template filled with the selected project's API values.
 * Fields the API does not return stay empty (highlighted on screen).
 */
export default function ProjectTemplateReport({ templateId, projectData, preparedBy }) {
  const { getAuthConfig, isReady } = useAuthFetch();
  const iframeRef = useRef(null);
  const [template, setTemplate] = useState(null);
  const [templateError, setTemplateError] = useState("");
  const [result, setResult] = useState(null);
  const [dataErrors, setDataErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  // 1) Load the saved template.
  useEffect(() => {
    let cancelled = false;
    setTemplate(null);
    setResult(null);
    setTemplateError("");
    if (!templateId) {
      setTemplateError("No report was chosen. Pick one from Nettles Links > Reports.");
      return undefined;
    }
    proposalTemplatesService
      .get(templateId)
      .then((rec) => {
        if (!cancelled) setTemplate(rec);
      })
      .catch((error) => {
        if (cancelled) return;
        const status = error?.response?.status;
        setTemplateError(
          status === 403
            ? "You do not have access to this saved report."
            : error?.response?.data?.message || error?.message || "Could not load this report."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  // 2) Load only the API sources the template needs, then fill it.
  const html = template?.html || "";
  const keys = useMemo(() => templateFieldKeys(html), [html]);

  useEffect(() => {
    if (!html || !isReady) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const ctx = await loadProjectFieldContext({
          opportunityId: projectData?.id_project,
          selected: projectData || {},
          getAuthConfig,
          sources: sourcesForKeys(keys),
          preparedBy,
        });
        if (cancelled) return;
        const filled = fillTemplateHtml(prepareHtml(html), ctx);
        setDataErrors(ctx.errors || {});
        setResult({ ...filled, html: withHint(filled.html) });
      } catch (error) {
        if (cancelled) return;
        console.error("Report data failed to load:", error);
        // Still show the template, with every field empty.
        const filled = fillTemplateHtml(prepareHtml(html), { raw: {}, selected: projectData || {}, meta: { preparedBy } });
        setDataErrors({ project: error?.message || "Could not load project data" });
        setResult({ ...filled, html: withHint(filled.html) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // projectData identity changes often; the ids below are what matter for a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, keys, isReady, projectData?.id_project, reloadTick]);

  const printReport = useCallback(() => {
    const frame = iframeRef.current?.contentWindow;
    if (!frame) return;
    frame.focus();
    frame.print();
  }, []);

  const downloadHtml = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    const source = doc ? `<!DOCTYPE html>\n${doc.documentElement.outerHTML}` : result?.html || "";
    if (!source) return;
    const blob = new Blob([source], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safe = `${template?.name || "report"} - ${projectData?.projectName || "project"}`.replace(/[^\w.-]+/g, "_");
    link.href = url;
    link.download = `${safe}.html`;
    link.click();
    URL.revokeObjectURL(url);
  }, [result, template, projectData?.projectName]);

  const failedSources = Object.keys(dataErrors);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 bg-stone-50 px-4 py-4 pb-8 text-stone-800 lg:px-6 min-[1025px]:pt-9">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-base font-bold text-stone-900">{template?.name || "Report"}</h2>
          <p className="truncate text-xs text-stone-500">
            {projectData?.projectName || "Selected project"}
            {projectData?.projectId ? ` · ${projectData.projectId}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setReloadTick((n) => n + 1)}
            disabled={loading || !template}
            className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-stone-100 disabled:opacity-50"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} /> Reload data
          </button>
          <button
            type="button"
            onClick={downloadHtml}
            disabled={!result}
            className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-stone-100 disabled:opacity-50"
          >
            <FiDownload /> Download
          </button>
          <button
            type="button"
            onClick={printReport}
            disabled={!result}
            className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            <FiPrinter /> Print / Save PDF
          </button>
        </div>
      </div>

      {templateError && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <FiAlertTriangle className="shrink-0" /> {templateError}
        </div>
      )}

      {!templateError && (loading || !result) && (
        <div className="text-sm text-stone-500">
          {template ? "Loading this project's data into the report..." : "Loading report..."}
        </div>
      )}

      {result && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-stone-200 bg-white px-3 py-2 text-xs text-stone-600">
          <span>
            <strong className="text-emerald-700">{result.filled}</strong> of {result.total} fields filled from the API
          </span>
          {result.total === 0 && (
            <span className="text-amber-700">
              This template has no API fields yet. Add some under Configuration &gt; Reports &amp; Proposal Templates &gt; API fields.
            </span>
          )}
          {result.missing.length > 0 && (
            <details className="min-w-0">
              <summary className="cursor-pointer text-amber-700">
                {result.missing.length} not available in the API (left empty)
              </summary>
              <ul className="mt-1 list-disc pl-5 text-stone-500">
                {result.missing.map((m, i) => (
                  <li key={`${m.key}-${i}`}>
                    {m.label} <span className="font-mono text-[10px] text-stone-400">{m.key}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {failedSources.length > 0 && (
            <span className="text-amber-700">
              Some data sources did not respond ({failedSources.join(", ")}). Reload to try again.
            </span>
          )}
        </div>
      )}

      {result && (
        <iframe
          ref={iframeRef}
          title={template?.name || "Report"}
          srcDoc={result.html}
          className="w-full flex-1 rounded-xl border border-stone-200 bg-white"
          style={{ minHeight: "calc(100vh - 16rem)" }}
        />
      )}
    </div>
  );
}
